import { useMemo } from "preact/compat"
import useSWR from "swr"
import { api_fetch_throws } from "@/api/client.tsx"

/** Lightweight category row returned by `GET /categories` (mirrors the Rust `CategoryOption`). */
export interface CategoryOption {
  id: string
  name: string
  source: string
  description: string
}

/**
 * Canonical high-level category names, used only as a fallback for an org that has no
 * categories of its own yet. Mirrors the server's `VALID_CATEGORIES` in
 * `server/src/agent/mod.rs`. Prefer `useOrgCategoryNames()` + `mergeCategoryOptions()`.
 */
export const DEFAULT_CATEGORIES = [
  "Contractual Fees",
  "Free Fill",
  "Retailer Promotion",
  "Freight",
  "Slotting",
  "Invoice Discrepancy",
  "Distributor Promotion",
  "Operational Issues",
  "Repayment",
  "Spoilage",
]

/**
 * The org's category names from `GET /categories` (swr, cached). The server already hides
 * categories the org has renamed or overridden (suppression filter), so a renamed-away name
 * never appears here.
 */
export function useOrgCategoryNames(): string[] {
  const { data } = useSWR<CategoryOption[]>("/categories", () =>
    api_fetch_throws<CategoryOption[]>("/categories", { use_cache: true })
  )
  return useMemo(() => (data ?? []).map(c => c.name).filter(Boolean), [data])
}

/**
 * Build the list of category names to offer in a picker/filter.
 *
 * `orgNames` (from `GET /categories`) is authoritative: it already excludes categories the org
 * renamed or overrode. The hardcoded `DEFAULT_CATEGORIES` are only used as a fallback for a fresh
 * org with no categories yet — unioning them for an established org would resurrect a name the org
 * renamed away (e.g. offering "Spoilage" again after it was renamed to "Damages", which
 * `update_category_inner` would re-create as a new org row).
 *
 * `extras` (e.g. the currently-selected value, or per-deduction descriptions) are always kept so
 * the current selection stays visible even if it isn't in the org menu.
 */
export function mergeCategoryOptions(orgNames: string[], extras: string[] = []): string[] {
  const base = orgNames.length > 0 ? orgNames : DEFAULT_CATEGORIES
  const set = new Set<string>()
  for (const name of [...base, ...extras]) {
    const trimmed = name?.trim()
    if (trimmed) set.add(trimmed)
  }
  return Array.from(set).sort()
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("mergeCategoryOptions", () => {
    it("falls back to defaults when the org has no categories", () => {
      expect(mergeCategoryOptions([])).toEqual([...DEFAULT_CATEGORIES].sort())
    })

    it("uses org names and does not resurrect a renamed-away default", () => {
      // Org renamed "Spoilage" -> "Damages"; GET /categories omits "Spoilage".
      const orgNames = ["Contractual Fees", "Damages", "Retailer Promotion"]
      const result = mergeCategoryOptions(orgNames)
      expect(result).toContain("Damages")
      expect(result).not.toContain("Spoilage")
    })

    it("keeps extras (e.g. the current value) even when absent from the org menu", () => {
      const result = mergeCategoryOptions(["Damages"], ["Legacy Category"])
      expect(result).toContain("Damages")
      expect(result).toContain("Legacy Category")
    })

    it("de-dupes, trims, and drops blanks", () => {
      const result = mergeCategoryOptions(["Freight", " Freight ", ""], ["  "])
      expect(result).toEqual(["Freight"])
    })
  })
}
